import { useCallback } from "react";

import { AccountViewer } from "components/form/graphql/types";
import useTypedContext from "hooks/useTypedContext";
import useSelfServiceFlags from "hooks/useSelfServiceFlags";
import { Action, Stack } from "types/generated";
import { AccessResult, hasAtLeastOneRequiredAction, NO_ACCESS_MESSAGE } from "utils/access";
import { AccountContext } from "views/AccountWrapper";

import { checkTemplateDeploymentAccess } from "./helpers/checkTemplateDeploymentAccess";
import useHasStackAdminAccess from "./useHasStackAdminAccess";
import useHasStackWriteAccess from "./useHasStackWriteAccess";

const getUnlockStackErrorMessage = (
  isLockedByMe: boolean,
  hasLowAccess: boolean,
  hasHighAccess: boolean
) => {
  if (!isLockedByMe && !hasHighAccess && hasLowAccess) {
    return "You don't have permission to unlock a stack locked by another user";
  }

  return NO_ACCESS_MESSAGE;
};

const FORCE_UNLOCK_ACCESS_TYPES = [Action.StackUnlockForce];

const hasForceUnlockAccess = (viewer: AccountViewer, spaceId: string) =>
  hasAtLeastOneRequiredAction(FORCE_UNLOCK_ACCESS_TYPES, viewer, spaceId);

const UNLOCK_ACCESS_TYPES = [Action.StackUnlock];

const hasUnlockAccess = (viewer: AccountViewer, spaceId: string) =>
  hasAtLeastOneRequiredAction(UNLOCK_ACCESS_TYPES, viewer, spaceId);

/**
 * Hook that returns a function to check if the current user can unlock a stack.
 * A user needs write access to unlock a stack they locked.
 * A user with manage access can unlock any locked stack.
 * A user can't unlock a stack that isn't locked.
 * Stacks created from a template deployment (blueprintDeployment) cannot be unlocked.
 * @returns {(stack: Stack) => AccessResult} Function that takes a stack and returns an AccessResult indicating if user can unlock it
 */
const useCanUnlockStack = (): ((stack: Stack) => AccessResult) => {
  const { viewer } = useTypedContext(AccountContext);
  const { enableSelfServiceV2 } = useSelfServiceFlags();

  const hasStackAdminAccessFn = useHasStackAdminAccess();
  const hasStackWriteAccessFn = useHasStackWriteAccess();

  return useCallback(
    (stack: Stack) => {
      const blueprintCheck = checkTemplateDeploymentAccess(enableSelfServiceV2, stack);
      if (blueprintCheck) {
        return blueprintCheck;
      }

      const isLocked = !!stack.lockedAt && !!stack.lockedBy;

      if (!isLocked) {
        return {
          result: false,
        };
      }

      const isLockedByMe = viewer.id === stack.lockedBy;
      const hasLowAccess =
        hasStackWriteAccessFn(stack).result ||
        hasUnlockAccess(viewer, stack.spaceDetails?.id || stack.space);

      const hasHighAccess =
        hasStackAdminAccessFn(stack).result ||
        hasForceUnlockAccess(viewer, stack.spaceDetails?.id || stack.space);

      const hasPermissionToUnlock = hasHighAccess || (isLockedByMe && hasLowAccess);

      if (hasPermissionToUnlock) {
        return {
          result: true,
        };
      }

      return {
        result: false,
        message: getUnlockStackErrorMessage(isLockedByMe, hasLowAccess, hasHighAccess),
      };
    },
    [hasStackAdminAccessFn, hasStackWriteAccessFn, viewer, enableSelfServiceV2]
  );
};

export default useCanUnlockStack;
